import { ActivityFilterKey } from '@gripp/shared-logic';
import { FilterButton, FilterType, useOpenedState } from '@gripp/shared-ui';
import { random } from 'lodash';
import { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { AccountFilterMenu } from '../../../accounts/components/accountFilterMenu';
import { AddOnFilterMenu } from '../../../add-ons/components/addOnFilterMenu';
import { AlertsManagementFilterMenu } from '../../../alerts/components/alertsManagementFilterMenu';
import { ActivityDateFilterMenu } from '../../../assets/components/activityDateFilterMenu';
import { ActivityGroupsFilterMenu } from '../../../assets/components/activityGroupsFilterMenu';
import { ActivityLabelsFilterMenu } from '../../../assets/components/activityLabelsFilterMenu';
import { AssetFilterMenu } from '../../../assets/components/assetFilterMenu';
import { AssetGroupsFilterMenu } from '../../../assets/components/assetGroupsFilterMenu';
import { TypesFilterMenu } from '../../../assets/components/typesFilterMenu';
import { FileCategoriesFilterMenu } from '../../../files/components/fileCategoriesFilterMenu';
import { FileFilterMenu } from '../../../files/components/fileFilterMenu';
import { GroupFilterMenu } from '../../../groups/components/groupFilterMenu';
import { RoutineFilterMenu } from '../../../new-routines/components/routineFilterMenu';
import { ProductFilterMenu } from '../../../products/components/productFilterMenu';
import { WorkspaceFilterMenu } from '../../../workspaces/components/workspaceFilterMenu';

type FilterBarProps = {
  filterButtonTypes: FilterType[];
  filterKey?: ActivityFilterKey;
};

export const FilterBar = ({ filterButtonTypes, filterKey }: FilterBarProps) => {
  const { t } = useTranslation();

  const {
    onOpen: onTypesFilterOpen,
    onClose: onTypesFilterClose,
    isOpened: isTypesFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onActivityGroupsFilterOpen,
    onClose: onActivityGroupsFilterClose,
    isOpened: isActivityGroupsFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onAssetGroupsFilterOpen,
    onClose: onAssetGroupsFilterClose,
    isOpened: isAssetGroupsFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onColumnsFilterOpen,
    onClose: onColumnsFilterClose,
    isOpened: isColumnsFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onGroupColumnsFilterOpen,
    onClose: onGroupColumnsFilterClose,
    isOpened: isGroupColumnsFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onAccountColumnsFilterOpen,
    onClose: onAccountColumnsFilterClose,
    isOpened: isAccountColumnsFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onAccountGroupsFilterOpen,
    onClose: onAccountGroupsFilterClose,
    isOpened: isAccountGroupsFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onAccountTypesFilterOpen,
    onClose: onAccountTypesFilterClose,
    isOpened: isAccountTypesFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onRoutineFilterOpen,
    onClose: onRoutineFilterClose,
    isOpened: isRoutineFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onProductFilterOpen,
    onClose: onProductFilterClose,
    isOpened: isProductFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onFileFilterOpen,
    onClose: onFileFilterClose,
    isOpened: isFileFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onFileCategoriesFilterOpen,
    onClose: onFileCategoriesFilterClose,
    isOpened: isFileCategoriesFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onActivityLabelsFilterOpen,
    onClose: onActivityLabelsFilterClose,
    isOpened: isActivityLabelsFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onActivityDateFilterOpen,
    onClose: onActivityDateFilterClose,
    isOpened: isActivityDateFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onAddOnFilterOpen,
    onClose: onAddOnFilterClose,
    isOpened: isAddOnFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onWorkspaceFilterOpen,
    onClose: onWorkspaceFilterClose,
    isOpened: isWorkspaceFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onAlertsManagementFilterOpen,
    onClose: onAlertsManagementFilterClose,
    isOpened: isAlertsManagementFilterOpened,
  } = useOpenedState();

  const {
    onOpen: onAlertsManagementStatusFilterOpen,
    onClose: onAlertsManagementStatusFilterClose,
    isOpened: isAlertsManagementStatusFilterOpened,
  } = useOpenedState();

  const [activeWorkspaceFilterType, setActiveWorkspaceFilterType] =
    useState<FilterType | null>(null);

  // Refs for dropdownview
  const typesButtonRef = useRef<View>(null);
  const groupsButtonRef = useRef<View>(null);
  const columnsButtonRef = useRef<View>(null);
  const groupColumnsButtonRef = useRef<View>(null);
  const accountColumnsButtonRef = useRef<View>(null);
  const accountGroupsButtonRef = useRef<View>(null);
  const accountTypesButtonRef = useRef<View>(null);
  const labelsButtonRef = useRef<View>(null);
  const dateButtonRef = useRef<View>(null);
  const routineButtonRef = useRef<View>(null);
  const productButtonRef = useRef<View>(null);
  const fileButtonRef = useRef<View>(null);
  const fileCategoriesButtonRef = useRef<View>(null);
  const addOnButtonRef = useRef<View>(null);
  const workspaceButtonRef = useRef<View>(null);
  const alertsManagementButtonRef = useRef<View>(null);
  const alertsManagementStatusButtonRef = useRef<View>(null);

  const HandleOpenTypes = (): ReactNode => {
    return (
      <TypesFilterMenu
        type={FilterType.ACTIVITYTYPES}
        filterKey={filterKey}
        isOpened={isTypesFilterOpened}
        onFilterOpen={onTypesFilterOpen}
        onFilterClose={onTypesFilterClose}
        anchorRef={typesButtonRef}
      />
    );
  };

  const HandleOpenAssetGroups = (): ReactNode => {
    return (
      <AssetGroupsFilterMenu
        isOpened={isAssetGroupsFilterOpened}
        onFilterOpen={onAssetGroupsFilterOpen}
        onFilterClose={onAssetGroupsFilterClose}
        anchorRef={groupsButtonRef}
        type={FilterType.ASSETGROUPS}
      />
    );
  };

  const HandleOpenColumns = (): ReactNode => {
    return (
      <AssetFilterMenu
        isOpened={isColumnsFilterOpened}
        onColumnFilterOpen={onColumnsFilterOpen}
        onColumnFilterClose={onColumnsFilterClose}
        anchorRef={columnsButtonRef}
        type={FilterType.ASSETCOLUMNS}
      />
    );
  };

  const HandleOpenGroupColumns = (): ReactNode => {
    return (
      <GroupFilterMenu
        isOpened={isGroupColumnsFilterOpened}
        onColumnFilterOpen={onGroupColumnsFilterOpen}
        onColumnFilterClose={onGroupColumnsFilterClose}
        anchorRef={groupColumnsButtonRef}
        type={FilterType.GROUPCOLUMNS}
      />
    );
  };

  const HandleOpenAccountGroups = (): ReactNode => {
    return (
      <AccountFilterMenu
        isOpened={isAccountGroupsFilterOpened}
        onAccountFilterOpen={onAccountGroupsFilterOpen}
        onAccountFilterClose={onAccountGroupsFilterClose}
        anchorRef={accountGroupsButtonRef}
        type={FilterType.ACCOUNTGROUPS}
      />
    );
  };

  const HandleOpenAccountTypes = (): ReactNode => {
    return (
      <AccountFilterMenu
        isOpened={isAccountTypesFilterOpened}
        onAccountFilterOpen={onAccountTypesFilterOpen}
        onAccountFilterClose={onAccountTypesFilterClose}
        anchorRef={accountTypesButtonRef}
        type={FilterType.ACCOUNTTYPES}
      />
    );
  };

  const HandleOpenAccountColumns = (): ReactNode => {
    return (
      <AccountFilterMenu
        isOpened={isAccountColumnsFilterOpened}
        onAccountFilterOpen={onAccountColumnsFilterOpen}
        onAccountFilterClose={onAccountColumnsFilterClose}
        anchorRef={accountColumnsButtonRef}
        type={FilterType.ACCOUNTCOLUMNS}
      />
    );
  };

  const HandleOpenActivityGroups = (): ReactNode => {
    return (
      <ActivityGroupsFilterMenu
        type={FilterType.ACTIVITYGROUPS}
        filterKey={filterKey}
        isOpened={isActivityGroupsFilterOpened}
        onFilterOpen={onActivityGroupsFilterOpen}
        onFilterClose={onActivityGroupsFilterClose}
        anchorRef={groupsButtonRef}
      />
    );
  };

  const HandleOpenActivityLabels = (): ReactNode => {
    return (
      <ActivityLabelsFilterMenu
        type={FilterType.ACTIVITYLABELS}
        filterKey={filterKey}
        isOpened={isActivityLabelsFilterOpened}
        onFilterOpen={onActivityLabelsFilterOpen}
        onFilterClose={onActivityLabelsFilterClose}
        anchorRef={labelsButtonRef}
      />
    );
  };

  const HandleOpenActivityDate = (): ReactNode => {
    return (
      <ActivityDateFilterMenu
        type={FilterType.ACTIVITYDATE}
        filterKey={filterKey}
        isOpened={isActivityDateFilterOpened}
        onFilterOpen={onActivityDateFilterOpen}
        onFilterClose={onActivityDateFilterClose}
        anchorRef={dateButtonRef}
      />
    );
  };

  const HandleOpenRoutine = (): ReactNode => {
    return (
      <RoutineFilterMenu
        isOpened={isRoutineFilterOpened}
        onRoutineFilterOpen={onRoutineFilterOpen}
        onRoutineFilterClose={onRoutineFilterClose}
        anchorRef={routineButtonRef}
        type={FilterType.ROUTINECOLUMNS}
      />
    );
  };

  const HandleOpenProduct = (): ReactNode => {
    return (
      <ProductFilterMenu
        isOpened={isProductFilterOpened}
        onProductFilterOpen={onProductFilterOpen}
        onProductFilterClose={onProductFilterClose}
        anchorRef={productButtonRef}
        type={FilterType.PRODUCTCOLUMNS}
      />
    );
  };

  const HandleOpenFile = (): ReactNode => {
    return (
      <FileFilterMenu
        isOpened={isFileFilterOpened}
        onFileFilterOpen={onFileFilterOpen}
        onFileFilterClose={onFileFilterClose}
        anchorRef={fileButtonRef}
        type={FilterType.FILECOLUMNS}
      />
    );
  };

  const HandleOpenFileCategories = (): ReactNode => {
    return (
      <FileCategoriesFilterMenu
        isOpened={isFileCategoriesFilterOpened}
        onFileCategoriesFilterOpen={onFileCategoriesFilterOpen}
        onFileCategoriesFilterClose={onFileCategoriesFilterClose}
        anchorRef={fileCategoriesButtonRef}
        type={FilterType.FILECATEGORIES}
      />
    );
  };

  const HandleOpenAddOn = (): ReactNode => {
    return (
      <AddOnFilterMenu
        isOpened={isAddOnFilterOpened}
        onAddOnFilterOpen={onAddOnFilterOpen}
        onAddOnFilterClose={onAddOnFilterClose}
        anchorRef={addOnButtonRef}
        type={FilterType.ADDONCOLUMNS}
      />
    );
  };

  const HandleOpenWorkspace = (): ReactNode => {
    return (
      <WorkspaceFilterMenu
        isOpened={isWorkspaceFilterOpened}
        onWorkspaceFilterOpen={onWorkspaceFilterOpen}
        onWorkspaceFilterClose={onWorkspaceFilterClose}
        anchorRef={workspaceButtonRef}
        type={activeWorkspaceFilterType || FilterType.WORKSPACECOLUMNS}
      />
    );
  };

  const HandleOpenAlertsManagement = (): ReactNode => {
    return (
      <AlertsManagementFilterMenu
        isOpened={isAlertsManagementFilterOpened}
        onFilterOpen={onAlertsManagementFilterOpen}
        onFilterClose={onAlertsManagementFilterClose}
        anchorRef={alertsManagementButtonRef}
        type={FilterType.ALERTSMANAGEMENTCOLUMNS}
      />
    );
  };

  const HandleOpenAlertsManagementStatus = (): ReactNode => {
    return (
      <AlertsManagementFilterMenu
        isOpened={isAlertsManagementStatusFilterOpened}
        onFilterOpen={onAlertsManagementStatusFilterOpen}
        onFilterClose={onAlertsManagementStatusFilterClose}
        anchorRef={alertsManagementStatusButtonRef}
        type={FilterType.ALERTSMANAGEMENTSTATUS}
      />
    );
  };

  const RenderFilterButtons = (): ReactNode => {
    let openFilterMenu = () => {};

    return filterButtonTypes.map((type, index) => {
      let ref = null;
      let filterKeyType = undefined;

      switch (type) {
        case FilterType.ACTIVITYTYPES:
          ref = typesButtonRef;
          filterKeyType = filterKey;
          openFilterMenu = onTypesFilterOpen;
          break;

        case FilterType.ACTIVITYGROUPS:
          ref = groupsButtonRef;
          filterKeyType = filterKey;
          openFilterMenu = onActivityGroupsFilterOpen;
          break;

        case FilterType.ACTIVITYLABELS:
          ref = labelsButtonRef;
          filterKeyType = filterKey;
          openFilterMenu = onActivityLabelsFilterOpen;
          break;

        case FilterType.ASSETGROUPS:
          ref = groupsButtonRef;
          openFilterMenu = onAssetGroupsFilterOpen;
          break;

        case FilterType.ASSETCOLUMNS:
          ref = columnsButtonRef;
          openFilterMenu = onColumnsFilterOpen;
          break;

        case FilterType.GROUPCOLUMNS:
          ref = groupColumnsButtonRef;
          openFilterMenu = onGroupColumnsFilterOpen;
          break;

        case FilterType.ACCOUNTCOLUMNS:
          ref = accountColumnsButtonRef;
          openFilterMenu = onAccountColumnsFilterOpen;
          break;

        case FilterType.ACCOUNTGROUPS:
          ref = accountGroupsButtonRef;
          openFilterMenu = onAccountGroupsFilterOpen;
          break;

        case FilterType.ACCOUNTTYPES:
          ref = accountTypesButtonRef;
          openFilterMenu = onAccountTypesFilterOpen;
          break;

        case FilterType.ACTIVITYDATE:
          ref = dateButtonRef;
          filterKeyType = filterKey;
          openFilterMenu = onActivityDateFilterOpen;
          break;

        case FilterType.ROUTINECOLUMNS:
          ref = routineButtonRef;
          openFilterMenu = onRoutineFilterOpen;
          break;

        case FilterType.PRODUCTCOLUMNS:
          ref = productButtonRef;
          openFilterMenu = onProductFilterOpen;
          break;

        case FilterType.FILECOLUMNS:
          ref = fileButtonRef;
          openFilterMenu = onFileFilterOpen;
          break;

        case FilterType.FILECATEGORIES:
          ref = fileCategoriesButtonRef;
          openFilterMenu = onFileCategoriesFilterOpen;
          break;

        case FilterType.ADDONCOLUMNS:
          ref = addOnButtonRef;
          openFilterMenu = onAddOnFilterOpen;
          break;

        case FilterType.WORKSPACECOLUMNS:
          ref = workspaceButtonRef;
          openFilterMenu = () => {
            setActiveWorkspaceFilterType(FilterType.WORKSPACECOLUMNS);
            onWorkspaceFilterOpen();
          };
          break;

        case FilterType.WORKSPACETIERS:
          ref = workspaceButtonRef;
          openFilterMenu = () => {
            setActiveWorkspaceFilterType(FilterType.WORKSPACETIERS);
            onWorkspaceFilterOpen();
          };
          break;

        case FilterType.ALERTSMANAGEMENTCOLUMNS:
          ref = alertsManagementButtonRef;
          openFilterMenu = onAlertsManagementFilterOpen;
          break;

        case FilterType.ALERTSMANAGEMENTSTATUS:
          ref = alertsManagementStatusButtonRef;
          openFilterMenu = onAlertsManagementStatusFilterOpen;
          break;

        default:
          openFilterMenu = () => {};
          break;
      }

      return (
        <View key={`${index}-filterBtn-${random}`} ref={ref}>
          <FilterButton
            type={type}
            filterKey={filterKeyType}
            onGoFilter={openFilterMenu}
          />
        </View>
      );
    });
  };

  return (
    <View style={[styles.filterBarContainer]}>
      <Text style={styles.filtersLabel}>{t('activity.filter.title')}: </Text>
      <RenderFilterButtons />

      {isTypesFilterOpened && <HandleOpenTypes />}
      {isActivityGroupsFilterOpened && <HandleOpenActivityGroups />}
      {isActivityLabelsFilterOpened && <HandleOpenActivityLabels />}
      {isAssetGroupsFilterOpened && <HandleOpenAssetGroups />}
      {isColumnsFilterOpened && <HandleOpenColumns />}
      {isGroupColumnsFilterOpened && <HandleOpenGroupColumns />}
      {isActivityDateFilterOpened && <HandleOpenActivityDate />}
      {isAccountGroupsFilterOpened && <HandleOpenAccountGroups />}
      {isAccountTypesFilterOpened && <HandleOpenAccountTypes />}
      {isAccountColumnsFilterOpened && <HandleOpenAccountColumns />}
      {isRoutineFilterOpened && <HandleOpenRoutine />}
      {isProductFilterOpened && <HandleOpenProduct />}
      {isFileFilterOpened && <HandleOpenFile />}
      {isFileCategoriesFilterOpened && <HandleOpenFileCategories />}
      {isAddOnFilterOpened && <HandleOpenAddOn />}
      {isWorkspaceFilterOpened && <HandleOpenWorkspace />}
      {isAlertsManagementFilterOpened && <HandleOpenAlertsManagement />}
      {isAlertsManagementStatusFilterOpened && (
        <HandleOpenAlertsManagementStatus />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  filterBarContainer: {
    height: 36,
    flexDirection: 'row',
    alignItems: 'center',
  },
  filtersLabel: {
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: -0.25,
    marginRight: 12,
  },
});
